import type {
  AiHandoffFigure,
  AiHandoffLandGroupingPayload,
  LandAnalysisSectionId,
} from '@popety-io/types'
import { buildSections, emptySections, specIds } from './sectionValues'
import { LAND_GROUPING_SECTION_SPECS } from './landGroupingSectionFields'
import { aiHandoffAbout } from './about'
import { AI_HANDOFF_CONNECT_URL } from './constants'

/**
 * A grouping share reuses the land section vocabulary — its share modal is
 * typed `LandAnalysisSectionId` and its ids come from the grouping
 * orchestrator's own extractor list, a subset of land's.
 */
const ALL_SECTION_IDS: LandAnalysisSectionId[] = [
  'summary',
  'history',
  'geo',
  'buildings',
  'zoning',
  'zoning_data',
  'restrictions',
  'purchase_market',
  'rental_market',
  'transport',
  'amenities',
  'noise',
  'sunlight',
  'demographics',
  'employment',
  'notes',
  'files',
]

const SUPPORTED_SECTIONS: LandAnalysisSectionId[] =
  specIds<LandAnalysisSectionId>(LAND_GROUPING_SECTION_SPECS)

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

const str = (v: unknown): string | null =>
  typeof v === 'string' && v ? v : null

/** Only the string members of a stored array; anything else is dropped. */
const strList = (v: unknown): string[] =>
  Array.isArray(v)
    ? v.filter((x): x is string => typeof x === 'string' && !!x)
    : []

const m2 = (v: unknown, asOf: string): AiHandoffFigure | null => {
  const value = num(v)
  return value === null
    ? null
    : {
        value: Math.round(value * 10) / 10,
        unit: 'm2',
        source: 'registre_foncier',
        as_of: asOf,
      }
}

export const buildLandGroupingAiPayload = (
  share: {
    value?: unknown
    section?: unknown
    land_grouping_id?: string | number
  },
  opts: { publicUrl: string; asOf: string }
): AiHandoffLandGroupingPayload => {
  const { asOf, publicUrl } = opts
  const value = (share.value ?? {}) as Record<string, any>
  const summary = (value.summary ?? {}) as Record<string, unknown>

  // Same storage shape as a land share: an ARRAY under `section` (singular).
  const requested = (
    Array.isArray(share.section) ? share.section : []
  ) as LandAnalysisSectionId[]
  const recognised = requested.filter((s) => ALL_SECTION_IDS.includes(s))
  const included = recognised.filter((s) => SUPPORTED_SECTIONS.includes(s))
  const omitted = recognised.filter((s) => !SUPPORTED_SECTIONS.includes(s))

  const coords = summary.geo_center as
    { lat?: unknown; lon?: unknown } | undefined
  const lat = num(coords?.lat)
  const lon = num(coords?.lon)

  const parcelNumbers = strList(summary.all_code_numbers)
  const single = str(summary.code_number)

  const sections = buildSections(
    value,
    included,
    asOf,
    LAND_GROUPING_SECTION_SPECS
  )

  return {
    schema_version: '1.0',
    about: aiHandoffAbout('land_grouping'),
    artifact: 'land_grouping',
    as_of: asOf,
    attribution: {
      source: 'Popety.io',
      url: publicUrl,
      connect_url: AI_HANDOFF_CONNECT_URL,
    },
    sections_included: included,
    sections_omitted: omitted,
    sections_empty: emptySections(
      included,
      sections,
      LAND_GROUPING_SECTION_SPECS
    ),
    // As on the other artifacts, identity is not gated on `summary`: a document
    // that names no subject is less safe to reason about, not more. Owner
    // names are never part of it — `summary.owners` carries named natural
    // persons, and only their count crosses, inside the summary section.
    identity: {
      land_grouping_id:
        str(summary.land_grouping_id) ??
        share.land_grouping_id?.toString() ??
        null,
      // Falls back to the single `code_number` so a two-parcel group whose
      // list is missing still names what it covers.
      parcel_numbers:
        parcelNumbers.length > 0 ? parcelNumbers : single ? [single] : [],
      addresses: strList(summary.all_addresses),
      address_full: str(summary.address_full),
      municipality_name: str(summary.municipality_name),
      coordinates: lat !== null && lon !== null ? { lat, lon } : null,
      parcel_count:
        parcelNumbers.length > 0 ? parcelNumbers.length : single ? 1 : null,
      // `total_area` is the group's summed geometry; `area` is the single-parcel
      // field kept for older rows that predate the totals.
      total_area: m2(summary.total_area ?? summary.area, asOf),
      total_official_area: m2(
        summary.total_official_area ?? summary.official_area,
        asOf
      ),
      main_zone_name: str(summary.main_lupa_name),
      main_zone_type: str(summary.main_lupa_type),
    },
    sections,
  }
}
